/**
 * Audio utility functions for playing notification sounds
 */
import notificationAudio from '../assets/audio/notification_audio.wav';

export const NOTIFICATION_SOUND_STORAGE_KEY = 'pmpi_silent_notification_sound';
const getNotificationSoundStorageKey = (projectId = null) => {
  if (projectId === null || projectId === undefined || projectId === '') {
    return null;
  }

  return `${NOTIFICATION_SOUND_STORAGE_KEY}_${projectId}`;
};

// Create a single audio instance that can be reused
let audioInstance = null;
let audioEnabled = false;

/**
 * Initialize audio instance
 */
const initAudio = () => {
  if (!audioInstance) {
    audioInstance = new Audio(notificationAudio);
    audioInstance.volume = 0.6;
    audioInstance.preload = 'auto';
  }
  return audioInstance;
};

/**
 * Check if audio is enabled
 */
export const isAudioEnabled = () => {
  return audioEnabled;
};

export const isNotificationSoundSilent = (projectId = null) => {
  if (typeof window === 'undefined') return false;
  const storageKey = getNotificationSoundStorageKey(projectId);
  if (!storageKey) return false;
  return window.localStorage.getItem(storageKey) === 'true';
};

export const hasStoredNotificationSoundPreference = (projectId = null) => {
  if (typeof window === 'undefined') return false;
  const storageKey = getNotificationSoundStorageKey(projectId);
  if (!storageKey) return false;
  return window.localStorage.getItem(storageKey) !== null;
};

export const getNotificationSoundDebugState = ({ projectId = null, ...overrides } = {}) => ({
  silent: isNotificationSoundSilent(projectId),
  unlocked: isAudioEnabled(),
  pending: false,
  lastPlaySuccess: null,
  lastPlaySource: 'none',
  projectId,
  ...overrides
});

export const setNotificationSoundSilent = (value, projectId = null) => {
  if (typeof window === 'undefined') return;
  const storageKey = getNotificationSoundStorageKey(projectId);
  if (!storageKey) return;
  window.localStorage.setItem(storageKey, String(Boolean(value)));
};

let sharedAudioContext = null;
let decodedAudioBuffer = null;
let isPreloadingBuffer = false;

const getAudioContext = () => {
  if (typeof window === 'undefined') return null;
  if (!sharedAudioContext) {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (AudioCtx) {
      sharedAudioContext = new AudioCtx();
    }
  }
  return sharedAudioContext;
};

const preloadAudioBuffer = async () => {
  if (decodedAudioBuffer || isPreloadingBuffer) return;
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    isPreloadingBuffer = true;
    const response = await fetch(notificationAudio);
    const arrayBuffer = await response.arrayBuffer();
    decodedAudioBuffer = await ctx.decodeAudioData(arrayBuffer);
  } catch (error) {
    // Silently handle preload fallback
  } finally {
    isPreloadingBuffer = false;
  }
};

/**
 * Enable audio after user interaction (required by browser autoplay policy)
 */
export const enableAudio = async () => {
  const audio = initAudio();
  const previousMuted = audio.muted;
  let audioUnlocked = false;

  const ctx = getAudioContext();
  if (ctx) {
    try {
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      if (ctx.state === 'running') {
        audioUnlocked = true;
        preloadAudioBuffer();
      }
    } catch (e) {
      // Ignore context resume errors
    }
  }
  
  try {
    // Unlock audio silently so the notification sound does not leak on first interaction.
    audio.muted = true;
    await audio.play();
    audio.pause();
    audio.currentTime = 0;
    audioUnlocked = true;
  } catch (error) {
    // HTML5 audio might fail in background/incognito but Web Audio API ctx may be running
  } finally {
    audio.muted = previousMuted;
  }

  audioEnabled = audioUnlocked || Boolean(ctx && ctx.state === 'running');
  return audioEnabled;
};

/**
 * Plays a notification sound using the notification audio file
 * @returns {Promise<boolean>} Whether playback started successfully
 */
export const playNotificationSound = async (projectId = null) => {
  try {
    const isSilent = isNotificationSoundSilent(projectId);

    if (isSilent) {
      return false;
    }

    const ctx = getAudioContext();

    // 1. Primary: Web Audio API Buffer (bypasses Chrome background tab HTMLMediaElement restrictions)
    if (ctx) {
      if (ctx.state === 'suspended') {
        try {
          await ctx.resume();
        } catch (e) {
          // Ignore resume failure
        }
      }

      if (ctx.state === 'running' || ctx.state === 'interrupted') {
        if (!decodedAudioBuffer) {
          await preloadAudioBuffer();
        }

        if (decodedAudioBuffer) {
          try {
            const source = ctx.createBufferSource();
            source.buffer = decodedAudioBuffer;
            const gainNode = ctx.createGain();
            gainNode.gain.value = 0.6;
            source.connect(gainNode);
            gainNode.connect(ctx.destination);
            source.start(0);
            return true;
          } catch (e) {
            // Fall through to synthesized tone
          }
        }

        playGeneratedNotificationSound();
        return true;
      }
    }

    // 2. Secondary: HTML5 Audio instance fallback
    const audio = initAudio();
    
    // Reset audio to start
    audio.currentTime = 0;
    audio.muted = false;
    audio.volume = 0.6;
    await audio.play();
    
    setTimeout(() => {
      audio.pause();
      audio.currentTime = 0;
    }, 2000);
    
    return true;
  } catch (error) {
    // 3. Fallback: Web Audio API synthesized tone
    try {
      playGeneratedNotificationSound();
      return true;
    } catch (e) {
      console.warn('⚠️ Notification audio playback blocked. Browser autoplay policy may be preventing sound.');
      return false;
    }
  }
};

/**
 * Plays a notification sound using Web Audio API (fallback)
 * Uses Web Audio API to generate a pleasant notification tone
 */
export const playGeneratedNotificationSound = () => {
  try {
    const audioContext = getAudioContext() || new (window.AudioContext || window.webkitAudioContext)();
    if (audioContext.state === 'suspended') {
      audioContext.resume().catch(() => {});
    }
    
    // Create oscillator for the notification sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Connect nodes
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Configure sound - pleasant notification tone
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime); // First tone
    oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1); // Second tone (higher)
    
    // Volume envelope - fade in and out
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
    gainNode.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.15);
    gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);
    
    // Play sound
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
    
    // Clean up
    oscillator.onended = () => {
      oscillator.disconnect();
      gainNode.disconnect();
    };
  } catch (error) {
    // Silently fail
  }
};

export const createNotificationSoundManager = ({
  onDebugStateChange,
  getProjectId = () => null,
  preserveMs = 1000,
  retryIntervalMs = 100
} = {}) => {
  let pendingNotificationSound = false;
  let pendingNotificationProjectId = null;
  let pendingNotificationSoundTimeoutId = null;
  let pendingNotificationSoundRetryId = null;
  let windowFocused = typeof document !== 'undefined' ? document.hasFocus() : true;
  let attached = false;
  let lastPlaySuccess = null;
  let lastPlaySource = 'none';

  const emitDebugState = (overrides = {}) => {
    const currentProjectId = overrides.projectId ?? getProjectId?.() ?? null;
    const nextState = getNotificationSoundDebugState({
      projectId: currentProjectId,
      pending: pendingNotificationSound,
      lastPlaySuccess,
      lastPlaySource,
      ...overrides
    });
    onDebugStateChange?.(nextState);
  };

  const isVisibleNotificationTab = () => (
    typeof document !== 'undefined'
    && document.visibilityState === 'visible'
    && !document.hidden
  );

  const clearPendingNotificationSound = () => {
    pendingNotificationSound = false;
    pendingNotificationProjectId = null;
    if (pendingNotificationSoundTimeoutId) {
      clearTimeout(pendingNotificationSoundTimeoutId);
      pendingNotificationSoundTimeoutId = null;
    }
    if (pendingNotificationSoundRetryId) {
      clearInterval(pendingNotificationSoundRetryId);
      pendingNotificationSoundRetryId = null;
    }
    emitDebugState({ pending: false });
  };

  const attemptNotificationSound = async (source, projectIdOverride = null) => {
    const projectId = projectIdOverride ?? getProjectId?.() ?? null;
    const didPlay = await playNotificationSound(projectId);
    lastPlaySuccess = didPlay;
    lastPlaySource = source;
    emitDebugState({ projectId });
    return didPlay;
  };

  const tryPlayPendingNotificationSound = async () => {
    if (!pendingNotificationSound) {
      return;
    }

    const didPlay = await attemptNotificationSound('pending', pendingNotificationProjectId);
    if (didPlay) {
      clearPendingNotificationSound();
    }
  };

  const handleWindowFocus = () => {
    windowFocused = true;
    setTimeout(() => {
      tryPlayPendingNotificationSound();
    }, 50);
  };

  const handleWindowBlur = () => {
    windowFocused = false;
  };

  const handleVisibilityChange = () => {
    if (isVisibleNotificationTab()) {
      setTimeout(() => {
        tryPlayPendingNotificationSound();
      }, 50);
    }
  };

  return {
    syncDebugState: (overrides = {}) => {
      emitDebugState(overrides);
    },
    attach: () => {
      if (attached || typeof window === 'undefined' || typeof document === 'undefined') {
        emitDebugState();
        return;
      }

      attached = true;
      window.addEventListener('focus', handleWindowFocus);
      window.addEventListener('blur', handleWindowBlur);
      document.addEventListener('visibilitychange', handleVisibilityChange);
      emitDebugState();
    },
    cleanup: () => {
      if (attached && typeof window !== 'undefined' && typeof document !== 'undefined') {
        window.removeEventListener('focus', handleWindowFocus);
        window.removeEventListener('blur', handleWindowBlur);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
      attached = false;
      clearPendingNotificationSound();
    },
    handleNewNotification: (newNotificationCount, projectId = null) => {
      if (!(newNotificationCount > 0)) return;

      const targetProjectId = projectId ?? getProjectId?.() ?? null;

      // Do NOT use requestAnimationFrame here because browser pauses rAF in hidden background tabs
      Promise.resolve().then(async () => {
        const didPlay = await attemptNotificationSound('direct', targetProjectId);
        if (didPlay) {
          clearPendingNotificationSound();
          return;
        }

        // If direct playback failed (e.g. autoplay restricted prior to first user gesture), queue pending retry
        pendingNotificationSound = true;
        pendingNotificationProjectId = targetProjectId;
        if (pendingNotificationSoundTimeoutId) {
          clearTimeout(pendingNotificationSoundTimeoutId);
        }
        if (pendingNotificationSoundRetryId) {
          clearInterval(pendingNotificationSoundRetryId);
        }
        emitDebugState({ pending: true, projectId: pendingNotificationProjectId });
        pendingNotificationSoundRetryId = setInterval(() => {
          tryPlayPendingNotificationSound();
        }, retryIntervalMs);
        pendingNotificationSoundTimeoutId = setTimeout(() => {
          clearPendingNotificationSound();
        }, preserveMs);
      });
    }
  };
};
